// Mock Entity Services for entities not yet implemented in backend
// Provides localStorage-based persistence for: Provider, Transaction, WebhookLog, RoutingRule, Settlement, AuditLog

import { graphqlClient } from './graphqlClient';

// Helper function to generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper function to get data from localStorage
const getLocalData = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return [];
  }
};

// Helper function to save data to localStorage
const setLocalData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

// Generic entity class
class MockEntity {
  constructor(storageKey, defaultFields = {}) {
    this.storageKey = storageKey;
    this.defaultFields = defaultFields;
  }

  async list(sortBy = '-created_date', limit = 1000) {
    let items = getLocalData(this.storageKey);

    // Sort items
    if (sortBy) {
      const isDescending = sortBy.startsWith('-');
      const field = isDescending ? sortBy.substring(1) : sortBy;

      items.sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];

        if (aVal < bVal) return isDescending ? 1 : -1;
        if (aVal > bVal) return isDescending ? -1 : 1;
        return 0;
      });
    }

    return items.slice(0, limit);
  }

  async filter(criteria) {
    const items = getLocalData(this.storageKey);
    return items.filter(item => {
      return Object.keys(criteria).every(key => {
        if (criteria[key] === undefined || criteria[key] === null) return true;
        return item[key] === criteria[key];
      });
    });
  }

  async get(id) {
    const items = getLocalData(this.storageKey);
    return items.find(item => item.id === id);
  }

  async create(data) {
    const items = getLocalData(this.storageKey);
    const newItem = {
      ...this.defaultFields,
      ...data,
      id: generateId(),
      created_date: new Date().toISOString(),
      updated_date: new Date().toISOString(),
    };
    items.push(newItem);
    setLocalData(this.storageKey, items);
    return newItem;
  }

  async update(id, data) {
    const items = getLocalData(this.storageKey);
    const index = items.findIndex(item => item.id === id);

    if (index === -1) {
      throw new Error(`${this.storageKey} with id ${id} not found`);
    }

    items[index] = {
      ...items[index],
      ...data,
      updated_date: new Date().toISOString(),
    };

    setLocalData(this.storageKey, items);
    return items[index];
  }

  async delete(id) {
    const items = getLocalData(this.storageKey);
    const filteredItems = items.filter(item => item.id !== id);

    if (filteredItems.length === items.length) {
      throw new Error(`${this.storageKey} with id ${id} not found`);
    }

    setLocalData(this.storageKey, filteredItems);
    return true;
  }
}

// Provider Entity
export const Provider = new MockEntity('mock_providers', {
  name: '',
  type: 'payment',
  status: 'active',
  priority: 0,
  config: {},
  limits: {
    min: 0,
    max: 100000,
  },
});

// Transaction Entity
export const Transaction = new MockEntity('mock_transactions', {
  amount: 0,
  currency: 'USD',
  status: 'pending',
  type: 'deposit',
  merchant_id: '',
  provider_id: '',
  reference: '',
  customer_email: '',
  customer_name: '',
  metadata: {},
});

// WebhookLog Entity
export const WebhookLog = new MockEntity('mock_webhook_logs', {
  url: '',
  method: 'POST',
  status_code: 200,
  request_body: {},
  response_body: {},
  attempts: 1,
  transaction_id: '',
  event_type: '',
});

// RoutingRule Entity
export const RoutingRule = new MockEntity('mock_routing_rules', {
  name: '',
  priority: 0,
  conditions: {},
  provider_id: '',
  merchant_id: '',
  enabled: true,
  description: '',
});

// Settlement Entity
export const Settlement = new MockEntity('mock_settlements', {
  amount: 0,
  currency: 'USD',
  status: 'pending',
  merchant_id: '',
  transaction_count: 0,
  settlement_date: new Date().toISOString(),
  reference: '',
  notes: '',
});

// AuditLog Entity
export const AuditLog = new MockEntity('mock_audit_logs', {
  user_id: '',
  action: '',
  entity_type: '',
  entity_id: '',
  changes: {},
  ip_address: '',
  user_agent: '',
});

// Real User Entities (from GraphQL backend)
class UserEntity {
  constructor(entityType) {
    this.entityType = entityType;
  }

  async list(sortBy = '-created_date', limit = 1000) {
    const queryMap = {
      Engine: 'getAllEngines',
      Reseller: 'getAllResellers',
      Merchant: 'getAllMerchants',
      Admin: 'getAllAdmins',
    };

    const queryName = queryMap[this.entityType];
    if (!queryName) {
      throw new Error(`Unknown entity type: ${this.entityType}`);
    }

    const query = `
      query GetAll${this.entityType}s {
        ${queryName} {
          id
          email
          name
          status
          createdAt
          updatedAt
          ${this.entityType === 'Engine' ? 'createdById' : ''}
          ${this.entityType === 'Reseller' ? 'createdByAdminId createdByEngineId' : ''}
          ${this.entityType === 'Merchant' ? 'createdByAdminId createdByEngineId createdByResellerId' : ''}
        }
      }
    `;

    const result = await graphqlClient.query(query);
    let items = result[queryName] || [];

    // Convert createdAt to created_date for sorting compatibility
    items = items.map(item => ({
      ...item,
      created_date: item.createdAt,
      updated_date: item.updatedAt,
    }));

    // Sort if needed
    if (sortBy) {
      const isDescending = sortBy.startsWith('-');
      const field = isDescending ? sortBy.substring(1) : sortBy;

      items.sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];

        if (aVal < bVal) return isDescending ? 1 : -1;
        if (aVal > bVal) return isDescending ? -1 : 1;
        return 0;
      });
    }

    return items.slice(0, limit);
  }

  async filter(criteria) {
    const items = await this.list();
    return items.filter(item => {
      return Object.keys(criteria).every(key => {
        if (criteria[key] === undefined || criteria[key] === null) return true;
        return item[key] === criteria[key];
      });
    });
  }

  async get(id) {
    const queryMap = {
      Engine: 'getEngineById',
      Reseller: 'getResellerById',
      Merchant: 'getMerchantById',
    };

    const queryName = queryMap[this.entityType];
    if (!queryName) {
      throw new Error(`Cannot get single ${this.entityType} by ID`);
    }

    const query = `
      query Get${this.entityType}($id: String!) {
        ${queryName}(id: $id) {
          id
          email
          name
          status
          createdAt
          updatedAt
          ${this.entityType === 'Engine' ? 'createdById' : ''}
          ${this.entityType === 'Reseller' ? 'createdByAdminId createdByEngineId' : ''}
          ${this.entityType === 'Merchant' ? 'createdByAdminId createdByEngineId createdByResellerId' : ''}
        }
      }
    `;

    const result = await graphqlClient.query(query, { id });
    return result[queryName];
  }

  async create(data) {
    const mutationMap = {
      Engine: 'createEngine',
      Reseller: 'createReseller',
      Merchant: 'createMerchant',
    };

    const mutationName = mutationMap[this.entityType];
    if (!mutationName) {
      throw new Error(`Cannot create ${this.entityType}`);
    }

    const inputType = `Create${this.entityType}Input`;

    const mutation = `
      mutation Create${this.entityType}($input: ${inputType}!) {
        ${mutationName}(input: $input) {
          id
          email
          name
          status
          createdAt
          updatedAt
        }
      }
    `;

    const result = await graphqlClient.mutate(mutation, { input: data });
    return result[mutationName];
  }

  async update(id, data) {
    const mutationMap = {
      Engine: 'updateEngine',
      Reseller: 'updateReseller',
      Merchant: 'updateMerchant',
    };

    const mutationName = mutationMap[this.entityType];
    if (!mutationName) {
      throw new Error(`Cannot update ${this.entityType}`);
    }

    const inputType = `Update${this.entityType}Input`;

    const mutation = `
      mutation Update${this.entityType}($id: String!, $input: ${inputType}!) {
        ${mutationName}(id: $id, input: $input) {
          id
          email
          name
          status
          createdAt
          updatedAt
        }
      }
    `;

    const result = await graphqlClient.mutate(mutation, { id, input: data });
    return result[mutationName];
  }

  async delete(id) {
    const mutationMap = {
      Engine: 'deleteEngine',
      Reseller: 'deleteReseller',
      Merchant: 'deleteMerchant',
    };

    const mutationName = mutationMap[this.entityType];
    if (!mutationName) {
      throw new Error(`Cannot delete ${this.entityType}`);
    }

    const mutation = `
      mutation Delete${this.entityType}($id: String!) {
        ${mutationName}(id: $id)
      }
    `;

    const result = await graphqlClient.mutate(mutation, { id });
    return result[mutationName];
  }
}

// Export real user entities
export const Engine = new UserEntity('Engine');
export const Reseller = new UserEntity('Reseller');
export const Merchant = new UserEntity('Merchant');
export const Admin = new UserEntity('Admin');

// Initialize with some sample data if localStorage is empty
const initializeSampleData = () => {
  if (!getLocalData('mock_providers').length) {
    Provider.create({
      name: 'Stripe',
      type: 'payment',
      status: 'active',
      priority: 1,
      config: { apiKey: 'sk_test_xxx' },
      limits: { min: 100, max: 100000 },
    });

    Provider.create({
      name: 'PayPal',
      type: 'payment',
      status: 'active',
      priority: 2,
      config: { clientId: 'xxx', secret: 'xxx' },
      limits: { min: 50, max: 50000 },
    });
  }

  if (!getLocalData('mock_transactions').length) {
    Transaction.create({
      amount: 1000,
      currency: 'USD',
      status: 'completed',
      type: 'deposit',
      merchant_id: 'merchant-1',
      provider_id: 'stripe',
      reference: 'TXN-001',
      customer_email: 'customer@example.com',
      customer_name: 'John Doe',
    });
  }

  if (!getLocalData('mock_routing_rules').length) {
    RoutingRule.create({
      name: 'Default Rule',
      priority: 1,
      conditions: { amount: { min: 0, max: 10000 } },
      provider_id: 'stripe',
      enabled: true,
      description: 'Route transactions under $10k to Stripe',
    });
  }
};

// Initialize sample data on module load
initializeSampleData();









